import type { StateCreator } from "zustand";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import type { IUser } from "../../types";
import type { IStoreState } from "../useStore";

export interface AuthSlice {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  userProfile: IUser | null;

  setAuth: (user: User | null, session: Session | null) => void;
  fetchUserProfile: () => Promise<void>;
}

export const createAuthSlice: StateCreator<
  IStoreState,
  [["zustand/devtools", never]],
  [],
  AuthSlice
> = (set, get) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  userProfile: null,

  setAuth: (user, session) => {
    const wasAuthenticated = get().isAuthenticated;
    set({
      user,
      session,
      isAuthenticated: !!user,
      loading: { ...get().loading, auth: false },
    });

    if (user && !wasAuthenticated) {
      get().fetchUserProfile();
      get().fetchTasks();
      get().fetchDependencies();
      get().subscribeToRealtime();
      get().subscribeToDependencyRealtime();
    }

    if (!user && wasAuthenticated) {
      get().unsubscribeFromRealtime();
      get().unsubscribeFromDependencyRealtime();
      set({
        tasks: [],
        recurringDefinitions: [],
        userProfile: null,
        tags: [],
        sharedTags: [],
        sharedTasks: [],
        activeTagFilter: null,
        dependencies: [],
      });
    }
  },

  fetchUserProfile: async () => {
    const { user } = get();
    if (!user) return;

    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("uuid", user.id)
      .single();

    if (error && error.code === "PGRST116") {
      const metadata = user.user_metadata;
      const { data: newUser, error: insertError } = await supabase
        .from("users")
        .insert({
          uuid: user.id,
          email: user.email || "",
          name: metadata?.full_name || metadata?.name || user.email || "",
          profile_photo_url: metadata?.avatar_url || null,
        })
        .select()
        .single();

      if (!insertError && newUser) {
        set({ userProfile: newUser });
      }
    } else if (data) {
      set({ userProfile: data });
    }

    // Seed default tags if user has none, then fetch tags + shared data
    await get().fetchTags();
    const { tags } = get();
    if (tags.length === 0) {
      await get().createTag("work", "#3b82f6");
      await get().createTag("personal", "#10b981");
      await get().fetchTags();
    }
    await get().fetchSharedTags();
    get().subscribeToSharedRealtime();
  },
});
